/**
 * The mode-neutral view model for the turn card (#306).
 *
 * Lessons, practice and check-ins all end up as the same `messages` list plus a few flags in
 * `useLessonState`. `buildTurnViewModel` reads that and says what the card shows right now; the
 * components only render it. It never calls the API or touches audio, so the audio and mic rules in
 * `.claude/rules/frontend.md` stay with the flows and `speech/`.
 */
import type { LessonPart } from '../hooks/useLessonApi';
import type { HelpLadder, Message } from '../hooks/useLessonState';

/** Words below this confidence are shown as low-confidence (#204 Phase 2). */
export const LOW_CONFIDENCE_THRESHOLD = 0.7;

export type RunMode = 'lesson' | 'practice' | 'checkin';
export type Speaker = 'them' | 'you';

/** What the card is for right now. */
export type TurnKind =
  /** A character speaks (a lesson persona line or a check-in line). */
  | 'persona'
  /** A new phrase to listen to before saying it. */
  | 'model'
  /** The learner's turn to speak. */
  | 'speak'
  /** The attempt did not come through and the learner may try again. */
  | 'retry'
  /** Nothing was heard. */
  | 'nospeech'
  /** The learner's attempt is being checked, or the next step is loading. */
  | 'working';

export interface FeedbackView {
  tone: 'ok' | 'retry';
  text: string;
  /** Per-word result of the learner's last attempt (BR-026), when the engine gave one. */
  words: Array<{ word: string; clear: boolean }>;
  /** Some words came through and some did not. */
  partial: boolean;
}

export interface ContextLineView {
  messageId: string;
  /** The character's line in the target language; never set in a check-in (BR-043). */
  targetText: string | null;
  /** What the character's line meant, once explained. */
  meaning: string | null;
  note: string | null;
}

export interface PartStripView {
  parts: Array<{
    id: string;
    label: string;
    state: 'done' | 'current' | 'todo';
  }>;
  /** Set when the strip collapses to a caption, e.g. "Pay · 3 of 6". */
  caption: string | null;
}

export interface TurnCardViewModel {
  kind: TurnKind;
  speaker: Speaker;
  /** The card's main line: the cue or the instruction. Empty for audio-only lines. */
  text: string;
  /** Target-language text the learner is learning (lessons and practice only). */
  targetText: string | null;
  note: string | null;
  /** Fixed label such as "Your choice". */
  label: string | null;
  /** The message whose clip this card plays, if any. */
  audioMessage: Message | null;
  /** True when this card plays the clip the run is waiting on (its end advances the run). */
  isCurrentPhrase: boolean;
  /**
   * The character's lines this turn answers, oldest first, each with its meaning. A lesson lists every
   * line the character spoke since the learner's last turn; a check-in only the latest, with no text (BR-043).
   */
  contexts: ContextLineView[];
  /** What a lesson persona line means, shown on its card without a click (BR-042); null in a check-in. */
  meaning: string | null;
  /** A scene beat to show above the card. */
  beat: string | null;
  feedback: FeedbackView | null;
  /** The mic may be used (mirrors `awaitingInput`). */
  canSpeak: boolean;
  /** "I need help" is offered: lessons and practice, not check-ins, while the phrase is still hidden (BR-052). */
  canGetHelp: boolean;
  /** The help the phrase has earned from misses and asks, if any (BR-051). */
  ladder: HelpLadder | null;
  /** "What did they say?" is offered on this card. */
  canAskHelp: boolean;
  /** Help lines to show on this card: a check-in's translation, context and phrase (BR-035). */
  help: string[];
  /** The part this card belongs to. */
  partId: string | null;
  strip: PartStripView;
}

export interface TurnViewInput {
  mode: RunMode;
  messages: Message[];
  parts: LessonPart[];
  awaitingInput: boolean;
  processing: boolean;
  currentPhraseId: string | null;
  /**
   * Check-in lines whose clip the learner has heard (or that could not play). A check-in stays on
   * the character's line until it is in this set, so the learner answers after hearing it (C-01).
   * Left out, no line is held.
   */
  heardIds?: ReadonlySet<string>;
}

/** More parts than this collapse the strip to a caption. */
export const MAX_STRIP_SEGMENTS = 4;

const CARD_KINDS = new Set([
  'play_audio',
  'request_production',
  'give_feedback',
  'no_speech',
]);

function lastIndex(messages: Message[], test: (m: Message) => boolean): number {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (test(messages[i])) return i;
  }
  return -1;
}

const isPersonaLine = (m: Message): boolean =>
  m.stepKind === 'play_audio' && (!!m.persona || !!m.audioOnly);

/** What a lesson persona line means, from the explanation that followed it (BR-042). */
function explanationOf(
  messages: Message[],
  lineIndex: number
): Message | undefined {
  const next = messages.findIndex((m, i) => i > lineIndex && isPersonaLine(m));
  return messages
    .slice(lineIndex + 1, next < 0 ? undefined : next)
    .find((m) => m.stepKind === 'explain_meaning');
}

/** The meaning and note a lesson persona card shows for its own line. */
function ownExplanation(
  messages: Message[],
  lineIndex: number
): { meaning: string | null; note: string | null } {
  const explained = explanationOf(messages, lineIndex);
  const line = messages[lineIndex];
  return {
    meaning: explained?.content ?? line.meaning ?? null,
    note: explained ? (explained.note ?? null) : (line.note ?? null),
  };
}

export function buildPartStrip(
  parts: LessonPart[],
  activePartId: string | null
): PartStripView {
  const activeIndex = parts.findIndex((p) => p.id === activePartId);
  const view = parts.map((p, i) => ({
    id: p.id,
    label: p.label,
    state:
      activeIndex === -1
        ? ('todo' as const)
        : i < activeIndex
          ? ('done' as const)
          : i === activeIndex
            ? ('current' as const)
            : ('todo' as const),
  }));
  const caption =
    parts.length > MAX_STRIP_SEGMENTS && activeIndex >= 0
      ? `${parts[activeIndex].label} · ${activeIndex + 1} of ${parts.length}`
      : null;
  return { parts: view, caption };
}

function feedbackFor(
  messages: Message[],
  feedbackIndex: number
): FeedbackView | null {
  if (feedbackIndex < 0) return null;
  const message = messages[feedbackIndex];
  const attempt = messages
    .slice(0, feedbackIndex)
    .reverse()
    .find((m) => m.sender === 'user');
  const words = (attempt?.words ?? []).map((w) => ({
    word: w.word,
    clear: w.confidence >= LOW_CONFIDENCE_THRESHOLD,
  }));
  const clear = words.filter((w) => w.clear).length;
  return {
    tone: message.success === false ? 'retry' : 'ok',
    text: message.content,
    words,
    partial: words.length > 1 && clear > 0 && clear < words.length,
  };
}

export function buildTurnViewModel(input: TurnViewInput): TurnCardViewModel {
  const {
    mode,
    messages,
    parts,
    awaitingInput,
    processing,
    currentPhraseId,
    heardIds,
  } = input;
  const isCard = (m: Message): boolean =>
    m.sender === 'tutor' && !!m.stepKind && CARD_KINDS.has(m.stepKind);
  let cardIndex = lastIndex(messages, isCard);
  const lastUserIndex = lastIndex(messages, (m) => m.sender === 'user');

  // A check-in line the learner has not heard yet keeps the card, so its clip is heard before the mic
  // opens and "What did they say?" has something to explain. Lines with no playable clip never hold.
  if (mode === 'checkin' && heardIds && cardIndex >= 0) {
    const lineIndex = lastIndex(messages, isPersonaLine);
    const line = lineIndex >= 0 ? messages[lineIndex] : null;
    if (
      line &&
      lineIndex < cardIndex &&
      lineIndex > lastUserIndex &&
      !!line.audioUrls?.natural &&
      !heardIds.has(line.id)
    ) {
      cardIndex = lineIndex;
    }
  }
  const previousCardIndex = lastIndex(
    messages.slice(0, Math.max(cardIndex, 0)),
    isCard
  );
  const card = cardIndex >= 0 ? messages[cardIndex] : null;
  const attemptPending = lastUserIndex > cardIndex;

  // The character's lines this turn answers, and what each meant. A lesson keeps every line the character
  // spoke since the learner's last turn (BR-042); a check-in only the latest, without its text (BR-043).
  const personaIndex = lastIndex(messages, isPersonaLine);
  const contextLine = (lineIndex: number): ContextLineView => {
    const line = messages[lineIndex];
    const own =
      mode === 'checkin'
        ? { meaning: null, note: null }
        : ownExplanation(messages, lineIndex);
    return {
      messageId: line.id,
      targetText: mode === 'checkin' ? null : (line.targetText ?? null),
      ...own,
    };
  };
  const lineIndexes: number[] = [];
  if (personaIndex >= 0 && messages[personaIndex] !== card) {
    lineIndexes.push(personaIndex);
    if (mode !== 'checkin') {
      for (let i = personaIndex - 1; i >= 0; i--) {
        const m = messages[i];
        if (m.sender === 'user') break;
        if (isPersonaLine(m)) lineIndexes.unshift(i);
        else if (CARD_KINDS.has(m.stepKind ?? '')) break;
      }
    }
  }
  const contexts = lineIndexes.map(contextLine);

  // A scene beat that came in since the previous card sets the scene for this one.
  const beatMessage =
    cardIndex >= 0
      ? messages
          .slice(previousCardIndex + 1, cardIndex)
          .reverse()
          .find((m) => m.stepKind === 'scene_beat')
      : undefined;
  const beat = beatMessage?.content ?? null;

  // A card with no part of its own (silence) stays in the part before it.
  const partId =
    card?.partId ??
    [...messages.slice(0, Math.max(cardIndex, 0))]
      .reverse()
      .find((m) => m.partId)?.partId ??
    null;

  // The phrase being learned stays replayable on the learner's turn (Again / Slower, BR-009): the last
  // modelled phrase, until an accepted attempt closes the turn.
  let replay: Message | null = null;
  if (mode !== 'checkin') {
    for (let i = cardIndex - 1; i >= 0; i--) {
      const m = messages[i];
      if (isPersonaLine(m)) break;
      if (m.stepKind === 'give_feedback' && m.success !== false) break;
      if (m.stepKind === 'play_audio') {
        replay = m;
        break;
      }
    }
  }

  // The translation, context and phrase a check-in sends after repeated failures (BR-035), until the
  // learner speaks again.
  const help = messages
    .slice(lastUserIndex + 1)
    .filter((m) => m.stepKind === 'help')
    .map((m) => m.content);

  const base = {
    help,
    audioMessage: null as Message | null,
    isCurrentPhrase: false,
    contexts,
    meaning: null as string | null,
    beat,
    feedback: null as FeedbackView | null,
    canSpeak: false,
    canGetHelp: false,
    ladder: null as HelpLadder | null,
    canAskHelp: false,
    partId,
    strip: buildPartStrip(parts, partId),
    text: '',
    targetText: null as string | null,
    note: null as string | null,
    label: null as string | null,
  };

  if (!card || (processing && attemptPending)) {
    return { ...base, kind: 'working', speaker: 'you', contexts: [] };
  }

  // Help is offered until the phrase itself has been shown (BR-052): lessons and practice only.
  const canGetHelpNow = (ladder: HelpLadder | null): boolean =>
    mode !== 'checkin' && awaitingInput && !ladder?.phrase;

  // Silence keeps the help the turn had already earned.
  const earlierLadder =
    mode !== 'checkin' && previousCardIndex >= 0
      ? (messages[previousCardIndex].ladder ?? null)
      : null;

  switch (card.stepKind) {
    case 'play_audio': {
      const persona = isPersonaLine(card);
      return {
        ...base,
        kind: persona ? 'persona' : 'model',
        speaker: persona ? 'them' : 'you',
        text: mode === 'checkin' ? '' : card.content,
        targetText: mode === 'checkin' ? null : (card.targetText ?? null),
        audioMessage: card,
        isCurrentPhrase: card.id === currentPhraseId,
        canAskHelp: persona,
        contexts: persona ? [] : contexts,
        ...(persona && mode !== 'checkin'
          ? ownExplanation(messages, cardIndex)
          : {}),
      };
    }
    case 'request_production': {
      // A success feedback just before this prompt rides along as a short ribbon.
      const ribbon =
        previousCardIndex > lastUserIndex &&
        messages[previousCardIndex].stepKind === 'give_feedback'
          ? feedbackFor(messages, previousCardIndex)
          : null;
      return {
        ...base,
        kind: 'speak',
        speaker: 'you',
        text: mode === 'checkin' ? '' : card.content,
        label: card.label ?? null,
        audioMessage: replay,
        targetText: mode === 'checkin' ? null : (card.ladder?.phrase ?? null),
        canSpeak: awaitingInput,
        ladder: mode === 'checkin' ? null : (card.ladder ?? null),
        canGetHelp: canGetHelpNow(card.ladder ?? null),
        canAskHelp: mode === 'checkin',
        feedback: ribbon && ribbon.tone === 'ok' ? ribbon : null,
      };
    }
    case 'give_feedback': {
      const feedback = feedbackFor(messages, cardIndex);
      const retry = feedback?.tone === 'retry';
      return {
        ...base,
        kind: retry ? 'retry' : 'working',
        speaker: 'you',
        text: '',
        targetText: card.targetText ?? null,
        audioMessage: replay,
        feedback,
        canSpeak: awaitingInput,
        ladder: card.ladder ?? null,
        canGetHelp: retry && canGetHelpNow(card.ladder ?? null),
        canAskHelp: mode === 'checkin',
      };
    }
    default:
      // no_speech: stay on the same turn and invite another attempt (BR-011)
      return {
        ...base,
        kind: 'nospeech',
        speaker: 'you',
        text: card.content,
        audioMessage: replay,
        canSpeak: awaitingInput,
        ladder: earlierLadder,
        targetText: earlierLadder?.phrase ?? null,
        canGetHelp: canGetHelpNow(earlierLadder),
        canAskHelp: mode === 'checkin',
      };
  }
}
